import { onDomReady } from '../utils/dom';

/*
 * Stumme Autoplay-Videos (Video-Element mit Autoplay, z. B. die 3D-Animation auf der Startseite).
 * Das autoplay-Attribut dient nur als Kennzeichnung: Gestartet wird erst, wenn das Video komplett
 * im Viewport steht, damit die Animation nicht schon halb verdeckt von vorne läuft. Pausiert wird
 * erst, wenn es ganz aus dem Bild ist, sonst würde jedes leichte Scrollen den Loop anhalten.
 * Jedes Video bekommt eine Pause-Taste, weil bewegte Inhalte, die länger als fünf Sekunden laufen,
 * anhaltbar sein müssen (WCAG 2.2.2). Hat der Nutzer pausiert, startet der Observer nicht wieder,
 * bis er selbst fortsetzt.
 * Bei "Bewegung reduzieren" läuft nichts von selbst los: das Video bleibt auf dem ersten Frame
 * stehen und bekommt die nativen Bedienelemente, damit es weiterhin abspielbar ist.
 */

const SELECTOR = 'video[autoplay]';
// Der Observer meldet beim Überschreiten der 1 gelegentlich 0,99x
const FULLY_VISIBLE = 0.99;
const LABELS = {
    de: { pause: 'Animation anhalten', play: 'Animation abspielen' },
    en: { pause: 'Pause animation', play: 'Play animation' },
};

// Videos, die der Nutzer selbst angehalten hat
const userPaused = new WeakSet();

function labels() {
    const language = document.documentElement.lang.slice(0, 2);

    return LABELS[language] ?? LABELS.en;
}

function disableAutoplay(video) {
    video.removeAttribute('autoplay');
    video.autoplay = false;
    video.pause();
}

function addToggleButton(video) {
    const text = labels();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'video-toggle-button';

    // Icon und Beschriftung folgen dem echten Abspielzustand, auch wenn der Observer pausiert
    const sync = () => {
        button.classList.toggle('is-paused', video.paused);
        button.setAttribute('aria-label', video.paused ? text.play : text.pause);
    };

    button.addEventListener('click', () => {
        if (video.paused) {
            userPaused.delete(video);
            video.play().catch(() => {});
        } else {
            userPaused.add(video);
            video.pause();
        }
    });

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    sync();
    video.after(button);
}

function playWhenFullyVisible(videos) {
    const observer = new IntersectionObserver(
        (entries) => {
            for (const { target, intersectionRatio } of entries) {
                if (intersectionRatio >= FULLY_VISIBLE && !userPaused.has(target)) {
                    // play() kann abgelehnt werden (z. B. Datensparmodus); dann bleibt das Poster stehen
                    target.play().catch(() => {});
                } else if (intersectionRatio === 0) {
                    target.pause();
                }
            }
        },
        { threshold: [0, 1] },
    );

    for (const video of videos) {
        // Der Browser hätte per autoplay-Attribut sofort gestartet; ab hier entscheidet der Observer
        disableAutoplay(video);
        addToggleButton(video);
        observer.observe(video);
    }
}

onDomReady(() => {
    const videos = [...document.querySelectorAll(SELECTOR)];

    if (!videos.length) {
        return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        for (const video of videos) {
            disableAutoplay(video);
            video.controls = true;
        }
        return;
    }

    playWhenFullyVisible(videos);
});
